import React, { useEffect, useState } from 'react';
import { getAdminComments, approveComment, deleteComment } from '../../services/api';
import { Check, Trash2, Loader2, MessageCircle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

const AdminComments = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  const load = () => {
    setLoading(true);
    getAdminComments()
      .then((r) => setComments(r.data || []))
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleApprove = async (c) => {
    await approveComment(c.id);
    load();
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`"${c.name}" tarafından yapılan yorumu silmek istediğinize emin misiniz?`)) return;
    await deleteComment(c.id);
    load();
  };

  const filtered = comments.filter((c) => filter === 'all' ? true : (filter === 'pending' ? !c.approved : c.approved));
  const pendingCount = comments.filter((c) => !c.approved).length;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#3D4A26]" size={32} /></div>;

  return (
    <div data-testid="admin-comments-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1B1B1B] flex items-center gap-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            <MessageCircle size={22} /> Blog Yorumları
          </h2>
          <p className="text-sm text-gray-500 mt-1">{pendingCount > 0 ? `${pendingCount} yorum onay bekliyor` : 'Bekleyen yorum yok'}</p>
        </div>
        <div className="flex bg-gray-100 rounded-lg p-1">
          {[
            { k: 'pending', l: `Bekleyen (${pendingCount})` },
            { k: 'approved', l: 'Onaylı' },
            { k: 'all', l: 'Hepsi' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              data-testid={`admin-comments-filter-${t.k}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md ${filter === t.k ? 'bg-white text-[#1B1B1B] shadow-sm' : 'text-gray-500 hover:text-[#1B1B1B]'}`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-500">Yorum yok</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <div key={c.id} className={`bg-white rounded-xl border p-5 ${c.approved ? 'border-green-100' : 'border-orange-100 bg-orange-50/30'}`} data-testid={`admin-comment-${c.id}`}>
              <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm text-[#1B1B1B]">{c.name}</p>
                    <span className="text-xs text-gray-400">•</span>
                    <p className="text-xs text-gray-500">{c.email}</p>
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.approved ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                      {c.approved ? 'ONAYLI' : 'BEKLEMEDE'}
                    </span>
                    <span className="text-xs text-gray-400">{fmt(c.created_at)}</span>
                    <Link to={`/blog/${c.postSlug}`} target="_blank" className="inline-flex items-center gap-1 text-xs text-[#3D4A26] hover:underline">
                      {c.postSlug} <ExternalLink size={10} />
                    </Link>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!c.approved && (
                    <button
                      onClick={() => handleApprove(c)}
                      data-testid={`admin-comment-approve-${c.id}`}
                      className="flex items-center gap-1 bg-green-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-green-600"
                    >
                      <Check size={12} /> Onayla
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(c)}
                    data-testid={`admin-comment-delete-${c.id}`}
                    className="flex items-center gap-1 bg-red-50 text-red-500 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-red-100"
                  >
                    <Trash2 size={12} /> Sil
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{c.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminComments;
